import {
  Children,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const DEFAULT_ITEM_GAP = 8;
const DEFAULT_ITEM_WIDTH = 160;
const DEFAULT_OVERSCAN = 1;
const DEFAULT_INITIAL_ITEM_COUNT = 4;

function normalizeItemWidths(itemWidth, items, itemCount) {
  if (typeof itemWidth !== 'function') {
    return Array.from({ length: itemCount }, () => itemWidth);
  }

  return Array.from({ length: itemCount }, (_, index) =>
    itemWidth(items[index], index)
  );
}

function buildVirtualMetrics(itemWidths, itemGap) {
  const offsets = [0];

  for (let index = 0; index < itemWidths.length; index += 1) {
    offsets.push(
      offsets[index] +
        itemWidths[index] +
        (index < itemWidths.length - 1 ? itemGap : 0)
    );
  }

  return {
    offsets,
    totalWidth: offsets[offsets.length - 1] || 0,
  };
}

function lowerBound(values, target) {
  let low = 0;
  let high = values.length;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (values[mid] < target) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  return low;
}

function findFirstVisibleIndex(offsets, itemWidths, scrollLeft) {
  let low = 0;
  let high = itemWidths.length;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    const itemEnd = offsets[mid] + itemWidths[mid];

    if (itemEnd <= scrollLeft) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  return Math.min(low, itemWidths.length);
}

function getVirtualRange(
  row,
  itemWidths,
  metrics,
  itemGap,
  overscan,
  initialItemCount
) {
  const itemCount = itemWidths.length;
  if (itemCount <= 0) return { start: 0, end: 0 };

  const clientWidth = row?.clientWidth || 0;
  const scrollLeft = row?.scrollLeft || 0;

  if (!clientWidth) {
    return {
      start: 0,
      end: Math.min(itemCount, initialItemCount),
    };
  }

  const viewportEnd = scrollLeft + clientWidth;
  const firstVisible = findFirstVisibleIndex(metrics.offsets, itemWidths, scrollLeft);
  const endExclusive = lowerBound(metrics.offsets, viewportEnd);
  const start = Math.max(0, firstVisible - overscan);
  const end = Math.min(
    itemCount,
    Math.max(endExclusive, firstVisible + 1) + overscan
  );

  return { start, end: Math.max(end, Math.min(itemCount, initialItemCount)) };
}

export default function HorizontalScrollRow({
  children,
  className = '',
  items,
  renderItem,
  itemKey,
  itemWidth = DEFAULT_ITEM_WIDTH,
  itemGap = DEFAULT_ITEM_GAP,
  overscan = DEFAULT_OVERSCAN,
  initialItemCount = DEFAULT_INITIAL_ITEM_COUNT,
  deferUntilVisible = true,
  visibilityRootMargin = '120px 0px',
  placeholderHeight,
  onScroll,
  ...props
}) {
  const rowRef = useRef(null);
  const isVirtual = Array.isArray(items) && typeof renderItem === 'function';
  const itemCount = isVirtual ? items.length : Children.count(children);
  const itemWidths = useMemo(
    () => (isVirtual ? normalizeItemWidths(itemWidth, items, itemCount) : []),
    [isVirtual, itemWidth, items, itemCount]
  );
  const metrics = useMemo(
    () => buildVirtualMetrics(itemWidths, itemGap),
    [itemGap, itemWidths]
  );
  const shouldDefer = isVirtual && deferUntilVisible;
  const [isVisible, setIsVisible] = useState(() => !shouldDefer);
  const [range, setRange] = useState(() =>
    getVirtualRange(
      null,
      itemWidths,
      metrics,
      itemGap,
      overscan,
      initialItemCount
    )
  );

  useEffect(() => {
    if (!shouldDefer) {
      setIsVisible(true);
      return undefined;
    }

    const row = rowRef.current;
    if (!row || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setIsVisible(true);
        observer.disconnect();
      },
      { rootMargin: visibilityRootMargin }
    );
    observer.observe(row);
    return () => observer.disconnect();
  }, [shouldDefer, visibilityRootMargin]);

  const updateVirtualRange = useCallback(() => {
    if (!isVirtual || !isVisible) return;
    setRange(
      getVirtualRange(
        rowRef.current,
        itemWidths,
        metrics,
        itemGap,
        overscan,
        initialItemCount
      )
    );
  }, [
    initialItemCount,
    isVirtual,
    isVisible,
    itemGap,
    itemWidths,
    metrics,
    overscan,
  ]);

  useEffect(() => {
    if (!isVirtual || !isVisible) return undefined;

    updateVirtualRange();
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(updateVirtualRange);
    observer.observe(row);
    return () => observer.disconnect();
  }, [isVirtual, isVisible, updateVirtualRange]);

  const virtualChildren = useMemo(() => {
    if (!isVirtual) return children;
    if (!isVisible) return null;

    const visibleItems = items.slice(range.start, range.end);
    const beforeWidth = metrics.offsets[range.start] || 0;
    const afterWidth = Math.max(0, metrics.totalWidth - (metrics.offsets[range.end] || 0));

    return (
      <>
        {beforeWidth > 0 && (
          <div
            aria-hidden="true"
            className="pointer-events-none flex-shrink-0"
            style={{ width: `${beforeWidth}px` }}
          />
        )}
        {visibleItems.map((item, offset) => {
          const index = range.start + offset;
          const width = itemWidths[index] || DEFAULT_ITEM_WIDTH;
          const key =
            typeof itemKey === 'function'
              ? itemKey(item, index)
              : item?.id ?? item?.slug ?? index;

          return (
            <div
              key={key}
              className="flex-shrink-0"
              style={{ width: `${width}px` }}
              data-virtual-index={index}
            >
              {renderItem(item, index)}
            </div>
          );
        })}
        {afterWidth > 0 && (
          <div
            aria-hidden="true"
            className="pointer-events-none flex-shrink-0"
            style={{ width: `${afterWidth}px` }}
          />
        )}
      </>
    );
  }, [
    children,
    isVirtual,
    isVisible,
    itemKey,
    itemWidths,
    items,
    metrics.offsets,
    metrics.totalWidth,
    range.end,
    range.start,
    renderItem,
  ]);

  const handleWheel = (event) => {
    const row = rowRef.current;
    if (!row || metrics.totalWidth <= row.clientWidth) return;

    const delta =
      Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (!delta) return;

    const maxScrollLeft = row.scrollWidth - row.clientWidth;
    const canScrollLeft = row.scrollLeft > 0;
    const canScrollRight = row.scrollLeft < maxScrollLeft - 1;
    if ((delta < 0 && !canScrollLeft) || (delta > 0 && !canScrollRight)) return;

    event.preventDefault();
    row.scrollLeft += delta;
  };

  const handleScroll = (event) => {
    updateVirtualRange();
    if (typeof onScroll === 'function') {
      onScroll(event);
    }
  };

  return (
    <div
      ref={rowRef}
      className={[
        'overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing',
        className,
      ].join(' ')}
      style={
        !isVisible && placeholderHeight
          ? { minHeight: `${placeholderHeight}px` }
          : undefined
      }
      onWheel={handleWheel}
      onScroll={handleScroll}
      {...props}
    >
      {virtualChildren}
    </div>
  );
}
